import { Alert, Tooltip, OverlayTrigger } from 'react-bootstrap';
import React, { Fragment, useState } from 'react';
import { Form } from 'react-bootstrap-5';
import PropTypes from 'prop-types';
import { isEmpty, pick } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSparkles, faInfoCircle, faComment } from '@fortawesome/pro-solid-svg-icons';
import { connect } from 'react-redux';

import AutocompletedAddressFormGroup from 'components/form/autocompleted_address_form_group';
import BrokersList from './brokers_list';
import ErrorAlert from './error_alert';
import { countryListPropType } from './prop_types';
import Button from 'shared/button';
import AiDocumentImport from './ai_document_import';

// Fields that can be populated by AI document import
const AI_POPULATED_FIELD_NAMES = [
  'address',
  'city',
  'state',
  'zip',
  'county',
  'country_id',
  'sale_or_lease',
  'name',
  'location_description',
  'property_type_id',
  'building_size',
  'building_class'
];

export function CreatePropertyForm({
  addressNotFound,
  aiPopulatedFieldsArray,
  allowUploadDoc,
  brokers,
  countryList,
  currentAiFieldsArray,
  errorMessages,
  formFields,
  gotoPrevStep,
  hasErrors,
  hasInsights,
  inferredFieldsArray,
  listingNotFound,
  maxBrokersLimit,
  newListing,
  propertyTypes,
  saving,
  showBrokerSelect,
  storeAiPopulatedFields,
  storeCurrentAiFields,
  storeInferredFields,
  onBrokerChange,
  onChange,
  onFormSubmit,
}) {
  // NOTE: when property isn't found in insights, this component is remounted (& state reset) due to the changed `key`,
  // so anything that needs to survive should be stored in redux.
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isFormAiPopulated, setIsFormAiPopulated] = useState(false);

  // Convert Redux arrays to Sets for efficient lookup
  // These are stored in Redux so they survive remounts when user clicks Back/Next or submits the form
  const aiPopulatedFields = new Set(aiPopulatedFieldsArray);
  const currentAiFields = new Set(currentAiFieldsArray);
  const inferredFields = new Set(inferredFieldsArray);

  // NOTE: converting to camelCase will introduce complexity & fragility because we're also dealing with arrays like
  // populatedFields and inferredFields; `camelized` (backend) doesn't handle those, nor does `api.ts` convert them, so
  // we'd need to use specialized logic
  //
  /* eslint-disable camelcase */
  const {
    address,
    city,
    country,
    state,
    zip,
    county,
    sale_or_lease,
    name,
    location_description,
    property_type_id,
    building_size,
    building_class
  } = formFields;
  /* eslint-enable camelcase */

  const isStateRequired = country.countryStates.length !== 0;
  const isZipRequired = country.zipLabel !== '';

  const isAddressInvalid = !address;
  const isCityInvalid = !city;
  const isCountryInvalid = !country?.hasOwnProperty('id');
  const isStateInvalid = isStateRequired && !state;
  const isZipInvalid = isZipRequired && !zip;
  const isPropertyTypeInvalid = !property_type_id; // eslint-disable-line camelcase
  const isBrokersInvalid = showBrokerSelect && brokers.some(item => isEmpty(item));

  const handleFormSubmit = (e) => {
    e.preventDefault();

    setFormSubmitted(true);

    const isFormInvalid = isAddressInvalid ||
      isCityInvalid ||
      isCountryInvalid ||
      isStateInvalid ||
      isZipInvalid ||
      isPropertyTypeInvalid ||
      isBrokersInvalid;

    if (!isFormInvalid) onFormSubmit();
  };

  const buttonText = () => {
    if (listingNotFound) {
      return saving ? 'Creating Listing...' : 'Create Listing';
    } else if (newListing) {
      return saving ? 'Searching...' : 'Search';
    }

    return '';
  };

  // Handle data from AI document import - mark fields as AI-populated
  const handleAiImport = (processedData) => {
    const aiFormData = processedData.propertyFormData;

    onChange(aiFormData);
    setIsFormAiPopulated(true);

    // Use populatedFieldNames from backend
    const populatedFields = processedData.populatedFieldNames || [];
    const updatedFields = new Set();

    // Use server-provided list of populated fields
    populatedFields.forEach((fieldName) => {
      if (AI_POPULATED_FIELD_NAMES.includes(fieldName)) {
        updatedFields.add(fieldName);
      }
    });

    if (updatedFields.size > 0) {
      const newAiPopulated = new Set([...aiPopulatedFields, ...updatedFields]);
      const newCurrentAi = new Set([...currentAiFields, ...updatedFields]);
      storeAiPopulatedFields(newAiPopulated);
      storeCurrentAiFields(newCurrentAi);
    }

    if (processedData.inferredFields && Array.isArray(processedData.inferredFields)) {
      storeInferredFields(new Set(processedData.inferredFields));
    }

    onChange({
      aiIngestMetadata: processedData.ingestMetadata,
      // store flag in redux to survive remounts
      showAiFeedbackForm: true
    });
  };

  // Handle manual field edits - clear the sparkles icon for that field
  // `fieldName` is the name of the field in AI_POPULATED_FIELD_NAMES
  const handleManualEdit = fieldName => (data) => {
    onChange(data);

    // Hide sparkles & inferred icon for this field
    // NOTE: for simplicity, if the user manually reverts their edit, we don't bring the icon back

    if (currentAiFields.has(fieldName)) {
      const updated = new Set(currentAiFields);
      updated.delete(fieldName);
      storeCurrentAiFields(updated);
    }

    if (inferredFields.has(fieldName)) {
      const updated = new Set(inferredFields);
      updated.delete(fieldName);
      storeInferredFields(updated);
    }
  };

  // Render sparkles icon if field was AI-populated and hasn't been manually edited
  const renderSparkles = (fieldName) => {
    if (!currentAiFields.has(fieldName)) return null;

    return (
      <FontAwesomeIcon
        className="mr-2"
        icon={faSparkles}
        style={{
          color: '#924FDC'
        }}
        title="AI-populated field"
      />
    );
  };

  // Render AI inferred icon if field was inferred or estimated
  const renderAiInferredIcon = (fieldName) => {
    if (!inferredFields.has(fieldName)) return null;

    const tooltip = (
      <Tooltip id={`ai-inferred-tooltip-${fieldName}`}>
        An exact match wasn&apos;t found in the document, so this value was predicted based on context.
        Please review for accuracy.
      </Tooltip>
    );

    return (
      <OverlayTrigger overlay={tooltip} placement="bottom">
        <span className="ml-2">
          <FontAwesomeIcon
            icon={faInfoCircle}
            style={{
              color: '#6c757d',
              cursor: 'help'
            }}
          />
        </span>
      </OverlayTrigger>
    );
  };

  // Helper to determine if an AI-optional field should be shown
  // These fields only appear if allowUploadDoc is enabled AND the field was populated by AI
  const shouldShowAiOptionalField = (fieldName) => {
    return allowUploadDoc && aiPopulatedFields.has(fieldName);
  };

  // these fields didn't exist before AI document import feature, so omit them for now
  // NOTE: other fields are also shown/hidden based on shouldShowAiOptionalField
  const renderAiOptionalFields = () => {
    return (
      <React.Fragment>
        {shouldShowAiOptionalField('sale_or_lease') && (
          <Form.Group className="mb-4">
            <Form.Label>
              {renderSparkles('sale_or_lease')}
              Sale or Lease
              {renderAiInferredIcon('sale_or_lease')}
            </Form.Label>
            {/* eslint-disable camelcase */}
            <Form.Select
              className="form-control"
              name="sale_or_lease"
              value={sale_or_lease || ''}
              onChange={e => handleManualEdit('sale_or_lease')({ sale_or_lease: e.target.value })}
            >
              <option value="">Select...</option>
              <option value="sale">Sale</option>
              <option value="lease">Lease</option>
            </Form.Select>
            {/* eslint-enable camelcase */}
          </Form.Group>
        )}

        {shouldShowAiOptionalField('name') && (
          <Form.Group className="mb-4">
            <Form.Label>
              {renderSparkles('name')}
              Property Name
              {renderAiInferredIcon('name')}
            </Form.Label>
            <Form.Control
              name="name"
              type="text"
              value={name || ''}
              onChange={e => handleManualEdit('name')({ name: e.target.value })}
            />
          </Form.Group>
        )}

        {shouldShowAiOptionalField('location_description') && (
          <Form.Group className="mb-4">
            <Form.Label>
              {renderSparkles('location_description')}
              Location Description
              {renderAiInferredIcon('location_description')}
            </Form.Label>
            {/* eslint-disable camelcase */}
            <Form.Control
              as="textarea"
              name="location_description"
              rows={3}
              value={location_description || ''}
              onChange={e => handleManualEdit('location_description')({ location_description: e.target.value })}
            />
            {/* eslint-enable camelcase */}
          </Form.Group>
        )}

        {shouldShowAiOptionalField('building_size') && (
          <Form.Group className="mb-4">
            <Form.Label>
              {renderSparkles('building_size')}
              Building Size (SF)
              {renderAiInferredIcon('building_size')}
            </Form.Label>
            {/* eslint-disable camelcase */}
            <Form.Control
              name="building_size"
              type="text"
              value={building_size || ''}
              onChange={e => handleManualEdit('building_size')({ building_size: e.target.value })}
            />
            {/* eslint-enable camelcase */}
          </Form.Group>
        )}

        {shouldShowAiOptionalField('building_class') && (
          <Form.Group className="mb-4">
            <Form.Label>
              {renderSparkles('building_class')}
              Building Class
              {renderAiInferredIcon('building_class')}
            </Form.Label>
            {/* eslint-disable camelcase */}
            <Form.Select
              className="form-control"
              name="building_class"
              value={building_class || ''}
              onChange={e => handleManualEdit('building_class')({ building_class: e.target.value })}
            >
              <option value="">Select a building class</option>
              <option value="A">A</option>
              <option value="B">B</option>
              <option value="C">C</option>
            </Form.Select>
            {/* eslint-enable camelcase */}
          </Form.Group>
        )}
      </React.Fragment>
    );
  };

  return (
    <Fragment>
      <h3 className="mb-5 mt-0 text-center" style={{ fontWeight: 'bold' }}>Create a new listing</h3>

      {newListing && (
        <Fragment>
          <div className="mb-5 spaced-text text-center">
            Search for a property and Buildout Insights will auto-populate the property information we have,
            or you can create a brand new listing.
          </div>

          {allowUploadDoc && (
            <AiDocumentImport saving={saving} onProcessedData={handleAiImport} />
          )}

          <h3 className="mb-4">Search for Property</h3>
        </Fragment>
      )}
      <ErrorAlert errorMessages={errorMessages} hasErrors={hasErrors}/>
      {listingNotFound && hasInsights && !hasErrors && (
        <Fragment>
          <Alert bsStyle="info" className="d-flex mb-5">
            <h4 className="m-0 alert-header">
              The address {addressNotFound} was not found in Insights. Creating new property.
            </h4>
          </Alert>

          <h3 className="mb-4">Create a new listing</h3>
        </Fragment>
      )}

      <Form autoComplete="off" noValidate onSubmit={handleFormSubmit}>
        {countryList.length > 1 && (
          <Form.Group className="mb-4">
            <Form.Label>
              {renderSparkles('country_id')}
              Country<span>*</span>
              {renderAiInferredIcon('country_id')}
            </Form.Label>
            <Form.Select
              autoFocus={countryList.length > 1}
              className="form-control"
              isInvalid={isCountryInvalid && formSubmitted}
              name="country"
              required
              value={country.id}
              onChange={(e) => {
                const selectedCountry = countryList.find(country => country.id === Number(e.target.value));
                handleManualEdit('country')({ country: selectedCountry });
              }}
            >
              {countryList.map(country => (
                <option key={country.id} value={country.id}>{country.name}</option>
              ))}
            </Form.Select>
            {isCountryInvalid && formSubmitted && <Form.Control.Feedback type="invalid">
              This field is required
            </Form.Control.Feedback>}
          </Form.Group>
        )}

        <AutocompletedAddressFormGroup
          {...pick(formFields, ['country', 'address', 'city', 'state', 'zip'])}
          feedback={isAddressInvalid && formSubmitted && 'This field is required'}
          feedbackType="invalid"
          hasError={isAddressInvalid && formSubmitted}
          label={
            <Form.Label>
              {renderSparkles('address')}
              Address<span>*</span>
              {renderAiInferredIcon('address')}
            </Form.Label>
          }
          required
          onChange={handleManualEdit('address')}
        />

        <Form.Group className="mb-4">
          <Form.Label>
            {renderSparkles('city')}
            City<span>*</span>
            {renderAiInferredIcon('city')}
          </Form.Label>
          <Form.Control
            isInvalid={isCityInvalid && formSubmitted}
            name="city"
            required
            type="text"
            value={city}
            onChange={e => handleManualEdit('city')({ city: e.target.value })}
          />
          {isCityInvalid && formSubmitted && <Form.Control.Feedback type="invalid">
            This field is required
          </Form.Control.Feedback>}
        </Form.Group>

        <Form.Group className="mb-4 row">
          {(country.countryStates.length !== 0) && (
            <div className="col-md-8">
              <Form.Label>
                {renderSparkles('state')}
                {country.stateLabel}<span>*</span>
                {renderAiInferredIcon('state')}
              </Form.Label>
              <Form.Select
                className="form-control"
                isInvalid={isStateInvalid && formSubmitted}
                name="state"
                required
                value={state}
                onChange={e => handleManualEdit('state')({ state: e.target.value })}
              >
                <option value="">Select a state</option>
                {country.countryStates.map(state => (
                  <option key={state.code} value={state.code}>{state.displayName}</option>
                ))}
              </Form.Select>
              {isStateInvalid && formSubmitted && <Form.Control.Feedback type="invalid">
                This field is required
              </Form.Control.Feedback>}
            </div>
          )}

          {country.zipLabel && (
            <div className="col-md-4">
              <Form.Label>
                {renderSparkles('zip')}
                {country.zipLabel}<span>*</span>
                {renderAiInferredIcon('zip')}
              </Form.Label>
              <Form.Control
                color="red"
                isInvalid={isZipInvalid && formSubmitted}
                name="zip"
                required
                type="string"
                value={zip}
                onChange={e => handleManualEdit('zip')({ zip: e.target.value })}
              />
              {isZipInvalid && formSubmitted && <Form.Control.Feedback type="invalid">
                This field is required
              </Form.Control.Feedback>}
            </div>
          )}
        </Form.Group>

        {shouldShowAiOptionalField('county') && country.hasCounty && (
          <Form.Group className="mb-4">
            <Form.Label>
              {renderSparkles('county')}
              County
              {renderAiInferredIcon('county')}
            </Form.Label>
            <Form.Control
              name="county"
              type="text"
              value={county || ''}
              onChange={e => handleManualEdit('county')({ county: e.target.value })}
            />
          </Form.Group>
        )}

        {showBrokerSelect && <Form.Group className="mb-4">
          <BrokersList
            brokers={brokers}
            isInvalid={isBrokersInvalid && formSubmitted}
            maxBrokersLimit={maxBrokersLimit}
            required={true}
            onSetBrokers={brokers => onBrokerChange(brokers)}
          />
          {isBrokersInvalid && formSubmitted && <Form.Control.Feedback type="invalid">
            This field is required
          </Form.Control.Feedback>}
        </Form.Group>}

        <Form.Group className="mb-4">
          <Form.Label>
            {renderSparkles('property_type_id')}
            Primary Property Type <span>*</span>
            {renderAiInferredIcon('property_type_id')}
          </Form.Label>
          {/* eslint-disable camelcase */}
          <Form.Select
            className="form-control"
            isInvalid={isPropertyTypeInvalid && formSubmitted}
            name="property_type_id"
            required
            value={property_type_id || ''}
            onChange={e => handleManualEdit('property_type_id')({ property_type_id: e.target.value })}
          >
            <option value="">Select a property type</option>
            {propertyTypes && Object.entries(propertyTypes).map(([key, value]) => (
              <option key={key} value={key}>{value}</option>
            ))}
          </Form.Select>
          {/* eslint-enable camelcase */}
          {isPropertyTypeInvalid && formSubmitted && <Form.Control.Feedback type="invalid">
            This field is required
          </Form.Control.Feedback>}
        </Form.Group>

        {renderAiOptionalFields()}

        {(isFormAiPopulated || formFields.showAiFeedbackForm) && (
          <Form.Group className="mb-4"
            style={{ borderRadius: '8px', background: '#DDEBFD', padding: '12px', gap: '8px' }}
          >
            <Form.Label>
              <FontAwesomeIcon className="mr-2" icon={faComment} />
              Help us improve your data import
            </Form.Label>
            <Form.Control
              as="textarea"
              name="aiFeedback"
              placeholder="How was your experience with the AI document import?"
              rows={3}
              value={formFields.aiFeedback || ''}
              onChange={e => onChange({ aiFeedback: e.target.value })}
            />
          </Form.Group>
        )}

        <div className="mt-5 d-flex justify-content-end">
          {gotoPrevStep && (
            <Button.Accent className="mr-3" outlined type="button" onClick={gotoPrevStep}>Back</Button.Accent>
          )}
          <Button.Primary disabled={saving} name="submit-listing" type="submit">{buttonText()}</Button.Primary>
        </div>
      </Form>
    </Fragment>
  );
}

CreatePropertyForm.propTypes = {
  addressNotFound: PropTypes.string.isRequired,
  aiPopulatedFieldsArray: PropTypes.array,
  allowUploadDoc: PropTypes.bool,
  brokers: PropTypes.array.isRequired,
  countryList: countryListPropType.isRequired,
  currentAiFieldsArray: PropTypes.array,
  errorMessages: PropTypes.object,
  formFields: PropTypes.object.isRequired,
  gotoPrevStep: PropTypes.func,
  hasErrors: PropTypes.bool.isRequired,
  hasInsights: PropTypes.bool.isRequired,
  inferredFieldsArray: PropTypes.array,
  listingNotFound: PropTypes.bool.isRequired,
  maxBrokersLimit: PropTypes.number.isRequired,
  newListing: PropTypes.bool.isRequired,
  propertyTypes: PropTypes.objectOf(PropTypes.string),
  saving: PropTypes.bool.isRequired,
  showBrokerSelect: PropTypes.bool.isRequired,
  storeAiPopulatedFields: PropTypes.func,
  storeCurrentAiFields: PropTypes.func,
  storeInferredFields: PropTypes.func,
  onBrokerChange: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onFormSubmit: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  propertyTypes: state.propertyTypes,
  // Get AI field tracking from Redux (stored as arrays, converted to Sets in component)

  // Fields that were originally AI-populated
  aiPopulatedFieldsArray: state.newPropertyFromScratchLocation?.aiPopulatedFields || [],
  // Fields that still have AI sparkles (i.e. haven't been manually edited)
  currentAiFieldsArray: state.newPropertyFromScratchLocation?.currentAiFields || [],
  inferredFieldsArray: state.newPropertyFromScratchLocation?.inferredFields || []
});

const mapDispatchToProps = dispatch => ({
  storeAiPopulatedFields: (fields) => {
    dispatch({ type: 'set_aiPopulatedFields', aiPopulatedFields: Array.from(fields) });
  },
  storeCurrentAiFields: (fields) => {
    dispatch({ type: 'set_currentAiFields', currentAiFields: Array.from(fields) });
  },
  storeInferredFields: (fields) => {
    dispatch({ type: 'set_inferredFields', inferredFields: Array.from(fields) });
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(CreatePropertyForm);
